import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { TrendingUp, AlertCircle, Sparkles } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';

const gradeConfig = {
  A: { bg: 'bg-emerald-500', bgLight: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-400', label: 'Excellent' },
  B: { bg: 'bg-green-500', bgLight: 'bg-green-50', text: 'text-green-700', bar: 'bg-green-400', label: 'Good' },
  C: { bg: 'bg-amber-500', bgLight: 'bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-400', label: 'Fair' },
  D: { bg: 'bg-orange-500', bgLight: 'bg-orange-50', text: 'text-orange-700', bar: 'bg-orange-400', label: 'Poor' },
  F: { bg: 'bg-red-500', bgLight: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-400', label: 'Bad' },
};

const Analytics = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAnalytics(); }, []);

  const loadAnalytics = async () => {
    try { const r = await api.get('/analytics/weekly'); setAnalytics(r.data); }
    catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  };

  if (loading) {
    return <DashboardLayout title="Weekly Analytics"><div className="flex items-center justify-center py-32"><div className="w-10 h-10 border-[3px] border-[#1B4332] border-t-transparent rounded-full animate-spin"></div></div></DashboardLayout>;
  }

  return (
    <DashboardLayout title="Weekly Analytics">
      {analytics?.total_scans === 0 ? (
        <div className="bg-white border border-[#1B4332]/8 rounded-xl p-12 text-center" data-testid="no-data">
          <TrendingUp className="w-12 h-12 text-[#1B4332]/10 mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="text-lg font-extrabold text-[#1B4332] mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>No Data Yet</h3>
          <p className="text-[13px] text-[#1B4332]/40 mb-5">Start scanning food to see your weekly health analytics</p>
          <Button onClick={() => navigate('/ingredient-checker')} className="rounded-lg font-semibold bg-[#1B4332] hover:bg-[#2D6A4F] text-white h-9 px-4 text-[13px] transition-all duration-200">Scan Your First Food</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Health Score */}
          <div className="bg-[#1B4332] rounded-xl p-6 text-white" data-testid="health-score">
            <div className="flex flex-col sm:flex-row items-center gap-5">
              <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center border-[3px] border-[#BFDB38] shrink-0">
                <div className="text-center">
                  <p className="text-3xl font-extrabold leading-none" style={{ fontFamily: 'Nunito, sans-serif' }}>{analytics.health_score}</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider font-medium mt-0.5">of 100</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-semibold text-[#BFDB38] mb-1">Overall Health Score</p>
                <p className="text-sm text-white/50">Based on {analytics.total_scans} scan{analytics.total_scans !== 1 ? 's' : ''} this week</p>
                <p className="text-[12px] text-white/30 mt-1">Higher is better · Aim for 80+</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-white border border-[#1B4332]/8 rounded-xl p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/35 mb-1">Total Scans</p>
              <p className="text-2xl font-extrabold text-[#1B4332]" style={{ fontFamily: 'Nunito, sans-serif' }}>{analytics.total_scans}</p>
            </div>
            <div className="bg-white border border-[#1B4332]/8 rounded-xl p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/35 mb-1">Top Brand</p>
              <p className="text-base font-extrabold text-[#1B4332] truncate" style={{ fontFamily: 'Nunito, sans-serif' }}>{analytics.most_scanned_brand}</p>
            </div>
          </div>

          {/* Grade Breakdown */}
          <div className="bg-white border border-[#1B4332]/8 rounded-xl p-5" data-testid="grade-breakdown">
            <h3 className="text-[11px] uppercase tracking-widest font-semibold text-[#1B4332]/40 mb-4">Food Grade Breakdown</h3>
            <div className="space-y-3">
              {['A', 'B', 'C', 'D', 'F'].map((grade) => {
                const count = analytics.grade_breakdown[grade] || 0;
                const pct = analytics.total_scans > 0 ? (count / analytics.total_scans) * 100 : 0;
                const config = gradeConfig[grade];
                return (
                  <div key={grade}>
                    <div className="flex justify-between items-center mb-1.5">
                      <div className="flex items-center gap-2.5">
                        <span className={`w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-bold text-white ${config.bg}`}>{grade}</span>
                        <div>
                          <span className="text-[13px] font-semibold text-[#1B4332]">{config.label}</span>
                        </div>
                      </div>
                      <span className="text-[12px] text-[#1B4332]/40 font-medium">{count} scan{count !== 1 ? 's' : ''} · {Math.round(pct)}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-[#1B4332]/5 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${config.bar}`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Concerning Ingredients */}
          {analytics.top_concerning_ingredients?.length > 0 && (
            <div className="bg-white border border-[#1B4332]/8 rounded-xl p-5" data-testid="concerning-ingredients">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-4 h-4 text-amber-500" strokeWidth={2} />
                <h3 className="text-[11px] uppercase tracking-widest font-semibold text-[#1B4332]/40">Top Concerning Ingredients</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {analytics.top_concerning_ingredients.map((ing, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="font-semibold text-[#1B4332] text-[13px]">{ing.name}</p>
                    <span className="text-[11px] text-amber-600 font-medium bg-amber-100 px-2 py-0.5 rounded-full">{ing.count}×</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pro Tip */}
          <div className="bg-[#1B4332]/5 border border-[#1B4332]/8 rounded-xl p-4 flex items-start gap-3">
            <Sparkles className="w-4 h-4 text-[#1B4332]/40 mt-0.5 shrink-0" strokeWidth={2} />
            <p className="text-[13px] text-[#1B4332]/60">
              <strong className="text-[#1B4332]">Pro Tip:</strong> Aim for mostly A and B grade foods for optimal cat health. If you see too many concerning ingredients, consider switching brands.
            </p>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Analytics;
